import dynamic from 'next/dynamic';

import { cn } from '@natu/utils/cn';
import type { PredefinedGAEvents } from '@natu/utils/events';

import {
  type MaxHeight,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
  type StoryblokLink,
  getAssetFromStoryblok,
  getLinkPropsFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
} from '../../../../utils';

const ConditionalLink = dynamic(() =>
  import('@natu/ui/ConditionalLink').then(mod => mod.ConditionalLink),
);

const AspectRatio = dynamic(() => import('@natu/ui/AspectRatio').then(mod => mod.AspectRatio));

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

interface SBImageProps {
  asset?: StoryblokAsset;
  aspectRatioX?: string;
  aspectRatioY?: string;
  priority?: boolean;
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  fullWidth?: boolean;
  width?: string;
  height?: string;
  maxHeight?: MaxHeight;
  rounded?: boolean;
  centered?: boolean;
  link?: StoryblokLink;
  screenReadersLabel?: string;
  event?: PredefinedGAEvents;
}

export const SBImage = ({ blok }: SBProps<SBImageProps>) => {
  const {
    asset,
    aspectRatioX,
    aspectRatioY,
    priority,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
    fullWidth,
    width: customWidth,
    height: customHeight,
    maxHeight,
    rounded,
    centered,
    link,
    screenReadersLabel,
    event,
  } = blok;

  const image = getAssetFromStoryblok(asset, { type: 'image' });

  const linkProps = getLinkPropsFromStoryblok(link);

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
    maxHeight,
    className: cn(
      fullWidth ? 'w-full' : 'w-auto',
      rounded && 'rounded-full',
      centered && 'mx-auto',
    ),
  });

  if (aspectRatioX && aspectRatioY) {
    if (!image?.src) return null;

    return (
      <ConditionalLink
        srLabel={screenReadersLabel}
        className={className}
        event={event}
        {...linkProps}
        {...sbEditable(blok)}
      >
        <AspectRatio style={{ width: '100%' }} ratio={Number(aspectRatioX) / Number(aspectRatioY)}>
          <ResponsiveImage priority={priority} className="max-h-full object-cover" {...image} />
        </AspectRatio>
      </ConditionalLink>
    );
  }

  const { alt, src, title, height, width } = image;

  if (!src) return null;

  return (
    <ConditionalLink
      srLabel={screenReadersLabel}
      className={className}
      event={event}
      {...linkProps}
      {...sbEditable(blok)}
    >
      <ResponsiveImage
        src={src}
        alt={alt}
        title={title}
        className={cn(fullWidth ? 'w-full' : 'w-auto', 'max-h-full')}
        width={Number(customWidth) || width}
        height={Number(customHeight) || height}
        priority={priority}
      />
    </ConditionalLink>
  );
};
